import { useEffect, useRef } from "react";

// Один общий поток событий chat-service на вкладку.
//
// `/api/chat/events` отдаёт ВСЕ события ВСЕМ подключённым: `{kind, ...}` в
// обычном `message`, плюс `: ping` каждые 15 с (комментарий SSE, до onmessage
// он вообще не доходит). Раньше поток открывал только ChatsPage, и то пока она
// на экране; входящему звонку нужна подписка, живущая всё время, пока открыто
// приложение, — и заводить под каждую такую подписку своё соединение незачем.
// Здесь соединение одно, подписчиков сколько угодно; последний ушедший его
// закрывает.
//
// ChatsPage свой EventSource пока держит сам: переводить её сюда — отдельное
// изменение со своими рисками (она монтируется до трёх раз разом).

export interface RealtimeEvent {
  kind?: string;
  [key: string]: unknown;
}

type Listener = (ev: RealtimeEvent) => void;
type OpenListener = () => void;

const EVENTS_URL = "/api/chat/events";
// EventSource.CLOSED — числом: так модуль не трогает глобальный класс при
// импорте (в тестах его подменяют уже после).
const CLOSED = 2;
const RETRY_MS = 5000;

let source: EventSource | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();
const openListeners = new Set<OpenListener>();

function ensureSource() {
  if (closeTimer !== null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  if (source) return;
  const es = new EventSource(EVENTS_URL, { withCredentials: true });
  es.onmessage = (m: MessageEvent) => {
    let data: unknown;
    try {
      data = JSON.parse(String(m.data));
    } catch {
      return; // не JSON — не наше событие
    }
    if (!data || typeof data !== "object" || Array.isArray(data)) return;
    // Копия: подписчик может отписаться прямо из обработчика.
    for (const fn of [...listeners]) {
      try {
        fn(data as RealtimeEvent);
      } catch (err) {
        // Сломанный подписчик не должен лишать событий остальных.
        console.error("realtime listener failed", err);
      }
    }
  };
  // onopen срабатывает и на первом подключении, и после каждого
  // автоматического переподключения (retry: 3000 от сервера). Пропущенные за
  // разрыв события не вернутся — подписчики по этому сигналу перечитывают
  // своё состояние целиком.
  es.onopen = () => {
    for (const fn of [...openListeners]) {
      try {
        fn();
      } catch (err) {
        console.error("realtime open listener failed", err);
      }
    }
  };
  // Обрыв сети браузер переподключает сам. А вот на не-200 ответ (502, пока
  // chat-service перезапускается при деплое) он закрывает поток насовсем — и
  // вкладка тихо перестала бы узнавать о звонках до перезагрузки.
  es.onerror = () => {
    if (source !== es || es.readyState !== CLOSED) return;
    source = null;
    if (retryTimer === null) {
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (!source && (listeners.size || openListeners.size)) ensureSource();
      }, RETRY_MS);
    }
  };
  source = es;
}

function releaseSource() {
  if (listeners.size || openListeners.size || closeTimer !== null) return;
  // Закрываем не сразу: StrictMode и перемонтирование отписываются и тут же
  // подписываются снова — без паузы это был бы лишний разрыв и новое
  // соединение на каждый такой цикл.
  closeTimer = setTimeout(() => {
    closeTimer = null;
    if (listeners.size || openListeners.size) return;
    source?.close();
    source = null;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  }, 0);
}

/** Подписка на события потока. Возвращает отписку. */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  ensureSource();
  return () => {
    listeners.delete(fn);
    releaseSource();
  };
}

/** Колбэк на каждое (пере)подключение потока — сигнал перечитать снимок. */
export function onOpen(fn: OpenListener): () => void {
  openListeners.add(fn);
  ensureSource();
  return () => {
    openListeners.delete(fn);
    releaseSource();
  };
}

/** Хук над subscribe/onOpen. Колбэки читаются через ref: их идентичность
 * меняется на каждой перерисовке, а переподписываться из-за этого незачем.
 *
 * Выключателя нарочно нет: подписка живёт, пока смонтирован компонент. Раньше
 * здесь был `enabled`, и окно входящих отписывалось, увидев `enabled: false`
 * от бекенда, — а включают приём перезапуском chat-service, и такая вкладка
 * этого уже не замечала до перезагрузки. */
export function useRealtime(onEvent: Listener, onReopen?: OpenListener): void {
  const ref = useRef({ onEvent, onReopen });
  ref.current = { onEvent, onReopen };
  useEffect(() => {
    const offEvent = subscribe((ev) => ref.current.onEvent(ev));
    const offOpen = onOpen(() => ref.current.onReopen?.());
    return () => {
      offEvent();
      offOpen();
    };
  }, []);
}
